import { useState } from "react";
import { Modal } from "../shared/Modal";
import { FormInput } from "../shared/FormInput";
import { Button } from "../shared/Button";
import { useApiCall } from "../../lib/useToastError";
import { AdminTiers } from "../../lib/apiTyped";

// Creates a brand new tier doc + Stripe Product/Price in one shot. Slug
// must be unique and URL-safe (lowercase / digits / dashes) so it works
// as a Firestore doc id and a stable identifier in API surfaces.
export function CreateTierModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [creditsM, setCreditsM] = useState("1");
  const [storageGB, setStorageGB] = useState("1");
  const [rpm, setRpm] = useState("120");
  const [artifacts, setArtifacts] = useState(true);
  const [prioritySupport, setPrioritySupport] = useState(false);
  const [busy, setBusy] = useState(false);

  const { run } = useApiCall();

  const slugLooksValid = /^[a-z0-9-]{2,40}$/.test(slug);
  const ready =
    !busy &&
    slugLooksValid &&
    name.trim().length > 0 &&
    Number.isFinite(Number(price)) &&
    Number(price) >= 0;

  async function submit() {
    if (!ready) return;
    setBusy(true);
    const r = await run(
      () =>
        AdminTiers.create({
          slug,
          name: name.trim(),
          pricePerSeatEur: Number(price),
          monthlyCreditsLimit: Math.round(Number(creditsM) * 1_000_000),
          storageBytesLimit: Math.round(Number(storageGB) * 1_073_741_824),
          requestsPerMinuteLimit: Math.round(Number(rpm)),
          features: { artifacts, prioritySupport },
        }),
      { successMessage: `Plan ${name.trim()} created and published in Stripe.` },
    );
    setBusy(false);
    if (r) onSaved();
  }

  return (
    <Modal onClose={onClose} maxWidth="32rem">
      <h2 className="text-xl font-black mb-1 pr-6 text-ink">Create new plan</h2>
      <p className="text-sm text-ink-3 mb-5">
        The Product + Price will be created in Stripe automatically. The slug is
        permanent — choose it carefully (e.g.: <code>tier-3</code>,{" "}
        <code>enterprise</code>). Credits and storage are <strong>per seat</strong>,
        applied as a single shared org pool (pool = per-seat × seatCount).
        Requests/minute is the per-member rate cap.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Slug (id)"
            value={slug}
            onChange={(v) => setSlug(v.toLowerCase())}
            placeholder="tier-3"
          />
          <FormInput
            label="Display name"
            value={name}
            onChange={setName}
            placeholder="Enterprise"
          />
        </div>
        <FormInput
          label="Price per seat (€ / month)"
          value={price}
          onChange={setPrice}
          type="number"
        />
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Credits / month per seat (millions)"
            value={creditsM}
            onChange={setCreditsM}
            type="number"
          />
          <FormInput
            label="Storage per seat (GB)"
            value={storageGB}
            onChange={setStorageGB}
            type="number"
          />
        </div>
        <FormInput
          label="Requests / minute (per member)"
          value={rpm}
          onChange={setRpm}
          type="number"
        />

        <div className="space-y-2 pt-2">
          <ToggleRow
            label="Artifacts"
            checked={artifacts}
            onChange={setArtifacts}
          />
          <ToggleRow
            label="Priority support"
            checked={prioritySupport}
            onChange={setPrioritySupport}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={submit} disabled={!ready} fullWidth>
          {busy ? "Creating…" : "Create plan"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-ink-2">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-ink"
      />
    </label>
  );
}
